<template>
  <!-- No BlockFieldset: it would disable the project export, which viewer may run. -->
  <div class="projects-export">
    <ButtonWait
      v-if="canRunProjectExport"
      :startCallback="startExportBuild"
      :pollCallback="fetchExportBuildState"
      class="pull-right"
    >
      <template v-slot:buttonText>
        {{ $t('projects_export') }}
      </template>
    </ButtonWait>

    <ButtonWait
      v-if="canRunDetailedExport"
      :startCallback="startDetailedExportBuild"
      :pollCallback="fetchDetailedExportBuildState"
      class="pull-right"
    >
      <template v-slot:buttonText>
        {{ $t('projects_export_detailed') }}
      </template>
    </ButtonWait>

    <ButtonWait
      v-if="canRunOfferExport"
      :startCallback="startOfferExportBuild"
      :pollCallback="fetchOfferExportBuildState"
      class="pull-right"
    >
      <template v-slot:buttonText>
        {{ $t('projects_offer_export') }}
      </template>
    </ButtonWait>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ButtonWait from '@/components/ButtonWait.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';

/**
 * Block projects-export (button group of the list header, see feature page projekte). Matrix-Authz:
 * viewer runs the project export, editor also the detailed export; the offer export is deprecated
 * and denied. Legacy-Authz: the old role rules decide. The block has no context object, so the
 * store actions stay in `auth`.
 */
export default {
  name: 'ProjectsExport',
  components: {
    ButtonWait,
  },
  mixins: [AccessBlockMixin],
  computed: {
    ...mapGetters('auth', [
      'isOrganizationRoleNavigationEnabled',
      'getCurrentUser',
      'isGuest',
      'isController',
      'isManagement',
      'isAdmin',
    ]),
    canRunProjectExport() {
      return this.isOrganizationRoleNavigationEnabled || !this.isGuest;
    },
    canRunDetailedExport() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return !this.isBlockReadOnly;
      }
      return this.isController || this.isManagement || this.isAdmin;
    },
    canRunOfferExport() {
      return !this.isOrganizationRoleNavigationEnabled && !this.isGuest;
    },
  },
  methods: {
    ...mapActions(['fetchUserAttachmentUrl']),
    ...mapActions('auth', [
      'buildUserProjectExport',
      'buildUserProjectExportDetailed',
      'refreshUser',
      'buildUserProjectOfferExport',
    ]),
    /**
     * Start export build on server
     * Is provided to ButtonWait as callback function
     */
    async startExportBuild() {
      if (this.getCurrentUser) {
        await this.buildUserProjectExport(this.getCurrentUser.id);
      }
    },
    /**
     * Start export build on server
     * Is provided to ButtonWait as callback function
     */
    async startOfferExportBuild() {
      if (this.getCurrentUser) {
        await this.buildUserProjectOfferExport(this.getCurrentUser.id);
      }
    },
    /**
     * Start detailed export build on server
     * Is provided to ButtonWait as callback function
     */
    async startDetailedExportBuild() {
      if (this.getCurrentUser) {
        await this.buildUserProjectExportDetailed(this.getCurrentUser.id);
      }
    },
    /**
     * Fetch build state from server (get's called once per second)
     * Is provided to ButtonWait as callback function
     */
    async fetchExportBuildState() {
      await this.refreshUser();

      if (this.getCurrentUser.projectExportBuildState === 'FINISHED') {
        this.downloadProjectExport('projectExportAttachment');
      }
      return this.getCurrentUser.projectExportBuildState;
    },

    /**
     * Fetch build state from server (get's called once per second)
     * Is provided to ButtonWait as callback function
     */
    async fetchOfferExportBuildState() {
      await this.refreshUser();
      if (this.getCurrentUser.projectOfferExportBuildState === 'FINISHED') {
        this.downloadProjectExport('projectOfferExportAttachment');
      }
      return this.getCurrentUser.projectOfferExportBuildState;
    },

    /**
     * Fetch detailed export build state from server (get's called once per second)
     * Is provided to ButtonWait as callback function
     */
    async fetchDetailedExportBuildState() {
      await this.refreshUser();
      if (this.getCurrentUser.projectExportDetailedBuildState === 'FINISHED') {
        this.downloadProjectExport('projectExportDetailedAttachment');
      }
      return this.getCurrentUser.projectExportDetailedBuildState;
    },

    /**
     * Download a finished export file
     * @param attachmentType
     */
    async downloadProjectExport(attachmentType) {
      const url = await this.fetchUserAttachmentUrl({
        userId: this.getCurrentUser.id,
        attachmentType: attachmentType,
      });

      window.open(url);
    },
  },
};
</script>

<style lang="scss" scoped>
// The buttons stay direct flex items of the list header.
.projects-export {
  display: contents;
}
</style>
